// Wire format for HTML ordinals talking to the Treechat wallet from inside their iframe.
//
// Standard calls use BRC-100's cross-document substrate (@bsv/sdk XDMSubstrate, picked by
// `new WalletClient('XDM')` or 'auto'): the ordinal posts
//   { type: 'CWI', isInvocation: true, id, call, args }
// to window.parent, and we answer
//   { type: 'CWI', isInvocation: false, id, status: 'success', result }
//   { type: 'CWI', isInvocation: false, id, status: 'error', description, code }
// Treechat-only calls (getContext, getViewer, getUsers, openProfile, openPost) use the same
// shape with type 'TREECHAT'.

export const CWI_MESSAGE_TYPE = 'CWI'
export const TREECHAT_MESSAGE_TYPE = 'TREECHAT'
export const ORDINAL_WALLET_VERSION = 'treechat-1.0.0'
// The rollout flag for the bridge. Users without the bridge get none: their ordinals see no
// wallet, as on any other site.
export const ORDINALS_WALLET_API_FEATURE = 'ordinals_wallet_api'

/** Central rollout policy for the bridge, the client side of User#ordinal_wallet_bridge_enabled?.
 * Keep callers isolated from the rollout mechanism so this can be toggled here
 * (userHasFeature(ORDINALS_WALLET_API_FEATURE)). */
export function ordinalWalletBridgeEnabled(): boolean {
    return true
}

// walletErrors in @bsv/sdk
export const WALLET_ERROR = {
    unknown: 1,
    unsupportedAction: 2,
    invalidParameter: 6,
    insufficientFunds: 7,
} as const

export const MAX_ACTION_OUTPUTS = 100
export const MAX_ACTION_SATS = 10_000_000
export const MAX_ACTION_INPUTS = 4
// Declared unlock lengths set the fee the viewer pays, so they are capped.
const MAX_UNLOCK_BYTES = 25_000
const MAX_TOTAL_UNLOCK_BYTES = 40_000
const MAX_INPUT_BEEF_BYTES = 256_000
const MAX_TOTAL_SCRIPT_BYTES = 200_000
const MAX_SCRIPT_HEX_LENGTH = 40_000

export type BridgeMessageType = typeof CWI_MESSAGE_TYPE | typeof TREECHAT_MESSAGE_TYPE

export interface BridgeInvocation {
    type: BridgeMessageType
    id: string
    call: string
    args: unknown
}

export type BridgeResponse =
    | {
          type: BridgeMessageType
          isInvocation: false
          id: string
          status: 'success'
          result: unknown
      }
    | {
          type: BridgeMessageType
          isInvocation: false
          id: string
          status: 'error'
          description: string
          code: number
      }

export class BridgeError extends Error {
    code: number

    constructor(message: string, code: number = WALLET_ERROR.unknown) {
        super(message)
        this.code = code
    }
}

export function parseInvocation(data: unknown): BridgeInvocation | null {
    if (typeof data !== 'object' || data === null) return null
    const message = data as Record<string, unknown>
    if (message.type !== CWI_MESSAGE_TYPE && message.type !== TREECHAT_MESSAGE_TYPE) return null
    if (message.isInvocation !== true) return null
    if (typeof message.id !== 'string' || message.id.length === 0 || message.id.length > 200) {
        return null
    }
    if (typeof message.call !== 'string' || message.call.length === 0) return null
    return { type: message.type, id: message.id, call: message.call, args: message.args }
}

export function successResponse(invocation: BridgeInvocation, result: unknown): BridgeResponse {
    return {
        type: invocation.type,
        isInvocation: false,
        id: invocation.id,
        status: 'success',
        result,
    }
}

export function errorResponse(invocation: BridgeInvocation, error: unknown): BridgeResponse {
    const code = error instanceof BridgeError ? error.code : WALLET_ERROR.unknown
    const description = error instanceof BridgeError ? error.message : 'Wallet operation failed'
    return {
        type: invocation.type,
        isInvocation: false,
        id: invocation.id,
        status: 'error',
        // XDMSubstrate drops descriptions over 4096 bytes.
        description: description.slice(0, 1000),
        code,
    }
}

export interface ActionOutput {
    lockingScript: string
    satoshis: number
    outputDescription?: string
}

/** An input the ordinal supplies and unlocks itself, after the wallet lays out the action. */
export interface ActionInput {
    txid: string
    vout: number
    unlockingScriptLength: number
    sequence: number
    inputDescription?: string
}

export interface ValidatedCreateAction {
    description: string
    outputs: ActionOutput[]
    totalSats: number
    returnTXIDOnly: boolean
    /** Set for a deferred action (signAndProcess: false): the ordinal's inputs and BEEF. */
    inputs?: ActionInput[]
    inputBEEF?: string
}

export interface ValidatedSignAction {
    reference: string
    /** Unlocking scripts (hex) by input index. */
    spends: Record<string, string>
}

function invalid(message: string): never {
    throw new BridgeError(message, WALLET_ERROR.invalidParameter)
}

function unsupported(message: string): never {
    throw new BridgeError(message, WALLET_ERROR.unsupportedAction)
}

function bytesToHex(value: unknown, what: string, maxBytes: number): string {
    if (!Array.isArray(value) || value.length === 0 || value.length > maxBytes) {
        invalid(`${what} must be a non-empty byte array`)
    }
    return (value as unknown[])
        .map(byte => {
            if (!Number.isInteger(byte) || (byte as number) < 0 || (byte as number) > 255) {
                invalid(`${what} must hold bytes`)
            }
            return (byte as number).toString(16).padStart(2, '0')
        })
        .join('')
}

function validateInputs(request: Record<string, unknown>): ActionInput[] {
    const inputs = request.inputs as unknown[]
    if (inputs.length > MAX_ACTION_INPUTS) invalid(`inputs must list 1-${MAX_ACTION_INPUTS} inputs`)
    const seen = new Set<string>()
    return inputs.map((input, index) => {
        if (typeof input !== 'object' || input === null) invalid(`inputs[${index}] is invalid`)
        const {
            outpoint,
            unlockingScriptLength,
            unlockingScript,
            sequenceNumber,
            inputDescription,
        } = input as Record<string, unknown>
        const match =
            typeof outpoint === 'string' ? /^([0-9a-f]{64})\.(\d+)$/i.exec(outpoint) : null
        if (!match) invalid(`inputs[${index}].outpoint must be "txid.vout"`)
        const key = `${match[1].toLowerCase()}.${match[2]}`
        if (seen.has(key)) invalid(`inputs[${index}] is listed twice`)
        seen.add(key)
        if (unlockingScript !== undefined) {
            unsupported('Unlock your inputs with signAction after createAction')
        }
        if (
            !Number.isSafeInteger(unlockingScriptLength) ||
            (unlockingScriptLength as number) < 1 ||
            (unlockingScriptLength as number) > MAX_UNLOCK_BYTES
        ) {
            invalid(`inputs[${index}].unlockingScriptLength must be 1-${MAX_UNLOCK_BYTES}`)
        }
        const sequence = sequenceNumber === undefined ? 0xffffffff : sequenceNumber
        if (
            !Number.isSafeInteger(sequence) ||
            (sequence as number) < 0 ||
            (sequence as number) > 0xffffffff
        ) {
            invalid(`inputs[${index}].sequenceNumber is invalid`)
        }
        return {
            txid: match[1].toLowerCase(),
            vout: Number(match[2]),
            unlockingScriptLength: unlockingScriptLength as number,
            sequence: sequence as number,
            inputDescription:
                typeof inputDescription === 'string' ? inputDescription.slice(0, 200) : undefined,
        }
    })
}

// Pays outputs from the viewer's wallet and broadcasts. A deferred action (signAndProcess:
// false) may also spend inputs the ordinal supplies and unlocks with signAction. Batching
// (noSend, sendWith) and baskets are refused rather than half-done.
export function validateCreateActionArgs(args: unknown): ValidatedCreateAction {
    if (typeof args !== 'object' || args === null) invalid('createAction args must be an object')
    const request = args as Record<string, unknown>

    const description = request.description
    if (typeof description !== 'string' || description.length < 5 || description.length > 2000) {
        invalid('description must be 5-2000 characters')
    }
    if (request.version !== undefined && request.version !== 1) unsupported('only version 1')
    if (request.lockTime !== undefined && request.lockTime !== 0) unsupported('only lockTime 0')

    const options = (request.options ?? {}) as Record<string, unknown>
    if (typeof options !== 'object' || options === null) invalid('options must be an object')
    const hasInputs = Array.isArray(request.inputs) && request.inputs.length > 0
    if (request.inputs !== undefined && !Array.isArray(request.inputs))
        invalid('inputs must be an array')
    const deferred = options.signAndProcess === false
    if (deferred && !hasInputs) unsupported('deferred signing needs inputs to unlock')
    if (hasInputs && !deferred) {
        unsupported('Spend your own inputs with signAndProcess: false, then signAction')
    }
    if (request.inputBEEF !== undefined && !hasInputs) invalid('inputBEEF without inputs')
    if (options.noSend === true) unsupported('noSend is not supported')
    if (Array.isArray(options.sendWith) && options.sendWith.length > 0) {
        unsupported('sendWith is not supported')
    }

    const outputs = request.outputs
    if (!Array.isArray(outputs) || outputs.length === 0 || outputs.length > MAX_ACTION_OUTPUTS) {
        invalid(`outputs must list 1-${MAX_ACTION_OUTPUTS} outputs`)
    }

    let totalSats = 0
    const validated = outputs.map((output, index): ActionOutput => {
        if (typeof output !== 'object' || output === null) invalid(`outputs[${index}] is invalid`)
        const { lockingScript, satoshis, outputDescription, basket } = output as Record<
            string,
            unknown
        >
        if (
            typeof lockingScript !== 'string' ||
            lockingScript.length > MAX_SCRIPT_HEX_LENGTH ||
            !/^(?:[0-9a-f]{2})+$/i.test(lockingScript)
        ) {
            invalid(`outputs[${index}].lockingScript must be hex`)
        }
        // Only a data (OP_RETURN) output may carry 0 satoshis.
        const minimum = describeLockingScript(lockingScript).kind === 'data' ? 0 : 1
        if (!Number.isSafeInteger(satoshis) || (satoshis as number) < minimum) {
            invalid(`outputs[${index}].satoshis must be a whole number of at least ${minimum}`)
        }
        // Baskets track outputs the wallet keeps; v1 only pays others.
        if (basket !== undefined) unsupported('output baskets are not supported')
        totalSats += satoshis as number
        return {
            lockingScript: lockingScript.toLowerCase(),
            satoshis: satoshis as number,
            outputDescription:
                typeof outputDescription === 'string' ? outputDescription.slice(0, 200) : undefined,
        }
    })
    if (totalSats > MAX_ACTION_SATS) invalid('total exceeds the per-action limit')
    const scriptBytes = validated.reduce((sum, output) => sum + output.lockingScript.length / 2, 0)
    if (scriptBytes > MAX_TOTAL_SCRIPT_BYTES) invalid('outputs are too large in total')

    const result: ValidatedCreateAction = {
        description,
        outputs: validated,
        totalSats,
        returnTXIDOnly: options.returnTXIDOnly === true,
    }
    if (hasInputs) {
        result.inputs = validateInputs(request)
        const declared = result.inputs.reduce((sum, input) => sum + input.unlockingScriptLength, 0)
        if (declared > MAX_TOTAL_UNLOCK_BYTES) invalid('the inputs declare too much unlock')
        result.inputBEEF = bytesToHex(request.inputBEEF, 'inputBEEF', MAX_INPUT_BEEF_BYTES)
    }
    return result
}

export function validateSignActionArgs(args: unknown): ValidatedSignAction {
    if (typeof args !== 'object' || args === null) invalid('signAction args must be an object')
    const { reference, spends, options } = args as Record<string, unknown>
    if (typeof reference !== 'string' || reference.length === 0) invalid('reference is required')
    // Signing always sends: refuse the options that would ask it not to.
    const signOptions = (options ?? {}) as Record<string, unknown>
    if (signOptions.noSend === true) unsupported('noSend is not supported')
    if (Array.isArray(signOptions.sendWith) && signOptions.sendWith.length > 0) {
        unsupported('sendWith is not supported')
    }
    if (typeof spends !== 'object' || spends === null || Array.isArray(spends)) {
        invalid('spends must map input indexes to unlocking scripts')
    }
    const entries = Object.entries(spends as Record<string, unknown>)
    if (entries.length === 0 || entries.length > MAX_ACTION_INPUTS) invalid('spends is empty')
    const unlocks: Record<string, string> = {}
    for (const [index, spend] of entries) {
        // Canonical input indexes ("0", "12"), as the server reads them.
        if (!/^(?:0|[1-9]\d?)$/.test(index)) invalid(`spends key ${index} is not an input index`)
        const unlockingScript = (spend as Record<string, unknown> | null)?.unlockingScript
        if (
            typeof unlockingScript !== 'string' ||
            unlockingScript.length === 0 ||
            unlockingScript.length > MAX_UNLOCK_BYTES * 2 ||
            !/^(?:[0-9a-f]{2})+$/i.test(unlockingScript)
        ) {
            invalid(`spends[${index}].unlockingScript must be hex`)
        }
        unlocks[index] = unlockingScript.toLowerCase()
    }
    return { reference, spends: unlocks }
}

export function validateAbortActionArgs(args: unknown): { reference: string } {
    const reference = (args as Record<string, unknown> | null)?.reference
    if (typeof reference !== 'string' || reference.length === 0) invalid('reference is required')
    return { reference }
}

// A short, human label for an output in the confirm sheet.
export function describeLockingScript(script: string): {
    kind: 'address' | 'data' | 'script'
    hash?: string
} {
    const lower = script.toLowerCase()
    const p2pkh = /^76a914([0-9a-f]{40})88ac$/.exec(lower)
    if (p2pkh) return { kind: 'address', hash: p2pkh[1] }
    if (lower.startsWith('6a') || lower.startsWith('006a')) return { kind: 'data' }
    return { kind: 'script' }
}

export function hexToBytes(hex: string): number[] {
    const bytes: number[] = []
    for (let i = 0; i < hex.length; i += 2) bytes.push(parseInt(hex.slice(i, i + 2), 16))
    return bytes
}
